import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Button } from './Button';

/**
 * EmptyState Component
 *
 * Reusable empty state display for lists, tables, and search results.
 * Shows an icon, title, description, and optional action button.
 *
 * @param {Object} props
 * @param {string} props.icon - Iconify icon name (e.g., 'lucide:inbox')
 * @param {string} props.title - Empty state title
 * @param {string} props.description - Empty state description
 * @param {string} props.actionText - Optional action button text
 * @param {Function} props.onAction - Optional action button callback
 * @param {string} props.actionIcon - Optional icon for action button
 */
export function EmptyState({
  icon = 'lucide:inbox',
  title,
  description,
  actionText,
  onAction,
  actionIcon,
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {/* Icon */}
      <div className="inline-flex items-center justify-center size-16 rounded-full bg-brand/3 mb-4">
        <Icon icon={icon} className="size-8 text-brand" />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-default mb-2">
        {title}
      </h3>

      {/* Description */}
      <p className="text-sm text-subtle mb-6 max-w-md">
        {description}
      </p>

      {/* Optional Action Button */}
      {actionText && onAction && (
        <Button
          variant="primary"
          onClick={onAction}
          icon={actionIcon ? <Icon icon={actionIcon} /> : null}
        >
          {actionText}
        </Button>
      )}
    </div>
  );
}

EmptyState.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  actionIcon: PropTypes.string,
};
